class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartDrawer = this.closest("cart-drawer");
      const addonLines = this.dataset.addons;
      if (addonLines) {
        cartDrawer.updateQuantities(addonLines, 0, this.dataset.index);
      } else {
        cartDrawer.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define("cart-drawer-remove-button", CartDrawerRemoveButton);

const RESPONSE_TEXT_CACHE_KEY = Symbol("cartAddonResponseText");
const FETCH_CONFIG_ERROR_STRING =
  "fetchConfig is missing (since it should always be loaded from global.js)";

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = document.getElementById("cart-drawer");
    this.cartDrawerOpen = document.querySelector(
      '[data-js-cart-drawer="cartDrawerOpen"]',
    );
    this.cartDrawerClose = document.querySelector(
      '[data-js-cart-drawer="cartDrawerClose"]',
    );
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.itemCountHolder = document.querySelector("[data-js-cart-item-count]");

    this.lineItemStatusElement = document.getElementById(
      "cart-drawer-line-item-status",
    );

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]'),
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));

    this.drawer.addEventListener(
      "keyup",
      (event) => event.code === "Escape" && this.close(),
    );
    this.querySelectorAll('[data-js-cart-drawer="cartDrawerClose"]').forEach(
      (closeButton) => {
        this.activeElement = document.activeElement || this.cartDrawerOpen;
        closeButton.addEventListener("click", this.close.bind(this));
      },
    );

    document
      .querySelectorAll('[data-js-cart-drawer="cartDrawerOpen"]')
      .forEach((openButton) => {
        openButton.addEventListener("click", this.handleDrawerOpen.bind(this));
      });

    // property label script here
    this.propertyLabelMap = {
      cover_colour: "Colour",
      cover_design: "Design",
      dob_day: "Day",
      dob_month: "Month",
      dob_year: "Year",
      name: "Name",
      dedication: "Dedication",
      inscription: "Dedication",
      surname: "Surname",
    };

    this.updatePropertyLabels = this.updatePropertyLabels.bind(this);
    this.hideEmptyAccordion();
    this.manageAddonsGiftboxVisibility();
  }

  /**
   * Update property labels for items in the cart
   */
  updatePropertyLabels() {
    this.drawer.querySelectorAll(".property-label").forEach((span) => {
      const key = span.textContent.trim().replace(":", "").toLowerCase();
      if (this.propertyLabelMap[key]) {
        span.textContent = `${this.propertyLabelMap[key]}:`;
      }
    });
    this.hideEmptyAccordion();
    this.manageAddonsGiftboxVisibility();
  }

  onChange(event) {
    const input = event.target;
    const addonLines = input.dataset.addons;
    if (addonLines) {
      this.updateQuantities(addonLines, input.value, input.dataset.index);
    } else {
      this.updateQuantity(
        event.target.dataset.index,
        event.target.value,
        document.activeElement.getAttribute("name"),
      );
    }
  }

  handleDrawerOpen() {
    if (theme.pageType === "cart") {
      return;
    }
    // Orphaned add-on lines can arrive while the drawer is closed (another
    // tab, external cart rebuilds). Sweep on open. While lines are being
    // removed the drawer stays disabled: removing a line renumbers the
    // index-based quantity/remove controls that were rendered before the
    // sweep, so a click against the stale DOM could hit the wrong line.
    this.getCart()
      .then((cart) => {
        const hasOrphans =
          Object.keys(window.CartAddonGuard.findOrphanUpdates(cart)).length >
          0;
        if (!hasOrphans) {
          return;
        }
        document
          .getElementById("cart-drawer")
          .classList.add("cart-drawer--disabled");
        return window.CartAddonGuard.sweepThenRender(
          cart,
          {
            sections: this.getSectionsToRender().map(
              (section) => section.section,
            ),
            sectionsUrl: window.location.pathname,
          },
          (finalState, didSweep) => {
            if (didSweep) {
              // renderContents re-enables the drawer via disableLoading().
              this.renderContents(finalState, false, null, null, true);
            } else {
              this.disableLoading();
            }
          },
        );
      })
      .catch(() => {
        this.disableLoading();
      });
    this.open();
  }

  handleAddedItem(addedItemResponse) {
    const fetchedCartData = this.getCart();
    this.handleCartData(fetchedCartData, addedItemResponse);
  }

  getCart() {
    return new Promise((resolve, reject) => {
      fetch(`${routes.ajax_api}`)
        .then((response) => response.json())
        .then((response) => {
          if (response) {
            resolve(response);
            return;
          }

          reject(response);
        })
        .catch((error) => {
          window.console.error(error);
        });
    });
  }

  handleCartData(promise, addedItemResponse = false) {
    Promise.all([promise])
      .then((values) => {
        if (values.length === 0) {
          return;
        }

        const cartData = values[0];

        if (addedItemResponse !== false) {
          const line = cartData.items.map((item, index) => {
            if (item.id === addedItemResponse.id) {
              return index;
            }
          });

          // The added-item sections HTML was rendered before the sweep, so
          // when the sweep removed something it is stale: render the swept
          // state with fresh drawer sections instead. isDrawerOpened stays
          // false on both paths so the drawer still opens after the add.
          return window.CartAddonGuard.sweepThenRender(
            cartData,
            {
              sections: this.getSectionsToRender().map(
                (section) => section.section,
              ),
              sectionsUrl: window.location.pathname,
            },
            (finalState, didSweep) => {
              if (didSweep) {
                const sweptLine = finalState.items.map((item, index) => {
                  if (item.id === addedItemResponse.id) {
                    return index;
                  }
                });
                this.renderContents(finalState, false, sweptLine, "", false);
                return;
              }
              this.renderContents(cartData, addedItemResponse, line, "", false);
            },
          );
        }
      })
      .catch((error) => {
        window.console.error(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        section: document.getElementById("cart-drawer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  /* 
    Dynamic way to:
      1. Find the key whose value matches a specific string (e.g. 'hn:deluxe-addon').
      2. Extract the last part of that key (after the last .).
      3. Then find all keys that contain that same ending substring (like "deluxe_edition").
    */
  findRelatedKeys(obj, value) {
    if (!obj || typeof obj !== "object") return [];
    if (value === undefined || value === null) return [];

    const matchedKey = Object.keys(obj).find((key) => obj[key] === value);
    if (!matchedKey) return [];
    const lastPart = String(matchedKey).split(".").pop();
    return Object.keys(obj).filter((key) => key.includes(lastPart));
  }

  updateQuantities(lines, quantity, index) {
    this.enableLoading(index);

    this.getCart()
      .then((cart) => {
        if (!cart) {
          return;
        }

        const linesObjAddOn = {};
        const mainProductlinesObj = {};
        let updateMainProduct = false;
        let propsToBeRemoved = [];
        let addOnSkuofPropsToBeRemoved = "";

        lines.split(",").forEach((line) => {
          const addon_item = cart.items.find(
            (item) =>
              item.key === line && item.product_type.toLowerCase() === "addon",
          );

          if (addon_item) {
            linesObjAddOn[line] = quantity;

            updateMainProduct = true;
            addOnSkuofPropsToBeRemoved =
              addon_item.sku || addon_item.product_title;
            propsToBeRemoved =
              Object.keys(addon_item.properties).length > 1
                ? Object.keys(addon_item.properties)
                : [];
          }

          if (updateMainProduct) {
            const main_item = cart.items.find(
              (item) =>
                item.key === line &&
                item.product_type.toLowerCase() !== "addon",
            );
            if (main_item) {
              mainProductlinesObj["id"] = line;
              mainProductlinesObj["quantity"] = main_item.quantity;

              // Build a copy of existing properties and remove keys we don't want to send
              const newProps = { ...(main_item.properties || {}) };
              // If add-on was added at personalisation level, then only have 1 property (_addon_id). So search from main product.
              this.findRelatedKeys(
                main_item.properties || {},
                addOnSkuofPropsToBeRemoved,
              ).forEach((key) => {
                if (key !== "_addon_id" && !propsToBeRemoved.includes(key)) {
                  propsToBeRemoved.push(key);
                }
              });

              propsToBeRemoved.forEach((key) => {
                if (key !== "_addon_id") {
                  /*
                  Special handling for 'addon.giftbox' and 'addon.alcohol' properties
                  to ensure they are set to specific values instead of being removed.
                  */
                  if (key === "addon.giftbox" || key === "addon.alcohol") {
                    // Transform value to expected format: lowercase, spaces/hyphens replaced with underscores
                    const formattedValue =
                      typeof newProps[key] === "string"
                        ? newProps[key].toLowerCase().replace(/[\s-]+/g, "_")
                        : newProps[key];

                    // For 'addon.alcohol', set to 'no-drinks' if not already
                    if (
                      key === "addon.alcohol" &&
                      formattedValue !== "no_drinks"
                    ) {
                      newProps[key] = "no-drinks";
                    }

                    // For 'addon.giftbox', set to 'No Presentation Box' if not already
                    if (
                      key === "addon.giftbox" &&
                      formattedValue !== "no_presentation_box"
                    ) {
                      newProps[key] = "No Presentation Box";
                    }
                  } else {
                    delete newProps[key];
                  }
                }
              });
              propsToBeRemoved = [];
              mainProductlinesObj["properties"] = newProps;
            }
          } else {
            linesObjAddOn[line] = quantity;
          }
        });

        this.updateMixedChanges(
          linesObjAddOn,
          mainProductlinesObj,
          index,
          updateMainProduct,
        );
      })
      .catch((error) => {
        window.console.error(error);
        this.disableLoading(index);
      });
  }

  updateMixedChanges(
    addonLinesObj,
    mainProductLineObj,
    index,
    updateMainProduct,
  ) {
    // When there's a parent line to re-key, strip its add-on properties first,
    // then remove the add-on. When there is NO parent line (e.g. the parent
    // was already removed in another tab), the add-on removal must STILL run:
    // otherwise updateMainProduct=true with an empty mainProductLineObj falls
    // through both paths, the clicked add-on is never removed, and it is left
    // stranded as an orphan.
    if (Object.keys(mainProductLineObj).length === 0) {
      this.callCartUpdateAPI(addonLinesObj, index);
      return;
    }

    const body = JSON.stringify(mainProductLineObj);
    if (typeof fetchConfig === "undefined") {
      throw new Error(FETCH_CONFIG_ERROR_STRING);
    }

    // Updating main product by removing add-on properties
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Cart change failed: ${response.status} ${text}`);
        }
        return response.text();
      })
      .then(() => {
        this.callCartUpdateAPI(addonLinesObj, index);
      })
      .catch((error) => {
        window.console.error(error);
        // Distinguish 'the parent line is gone' (stale key: removed or
        // re-keyed in another tab) from a transient failure with the parent
        // intact. Only the former may proceed to remove the clicked add-on;
        // after a transient failure the removal would leave the parent still
        // advertising the add-on in its properties with no matching line, so
        // leave both untouched and retryable instead.
        this.getCart()
          .then((cart) => {
            const parentStillPresent =
              cart &&
              Array.isArray(cart.items) &&
              cart.items.some((item) => item.key === mainProductLineObj.id);
            if (parentStillPresent) {
              this.disableLoading(index);
              return;
            }
            this.callCartUpdateAPI(addonLinesObj, index);
          })
          .catch(() => {
            this.disableLoading(index);
          });
      });
  }

  callCartUpdateAPI(addonLinesObj, index) {
    if (Object.keys(addonLinesObj).length === 0) {
      return;
    }

    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    const sectionsUrl = window.location.pathname;
    const body = JSON.stringify({
      updates: {
        ...addonLinesObj,
      },
      sections,
      sections_url: sectionsUrl,
    });

    if (typeof fetchConfig === "undefined") {
      throw new Error(FETCH_CONFIG_ERROR_STRING);
    }

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        // Backstop: remove any orphaned add-on lines surfaced by this update
        // (e.g. the parent vanished in another tab during the change/update
        // gap) before rendering, so they never survive to checkout. Returned
        // into the chain so a render-phase throw still reaches the .catch
        // below.
        return window.CartAddonGuard.sweepThenRender(
          parsedState,
          { sections, sectionsUrl },
          (finalState) => {
            this.renderContents(finalState, false, index, null, true);
            this.updateTrappedFocus();
          },
        );
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        this.disableLoading(index);
      });
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    const sectionsUrl = window.location.pathname;
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: sectionsUrl,
    });

    if (typeof fetchConfig === "undefined") {
      throw new Error(FETCH_CONFIG_ERROR_STRING);
    }
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        return window.CartAddonGuard.sweepThenRender(
          parsedState,
          { sections, sectionsUrl },
          (finalState) => {
            this.renderContents(finalState, false, line, name, true);

            if (quantity == 0) {
              this.updateTrappedFocus();
            }
          },
        );
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden"),
        );
        // document.getElementById("cart-drawer-errors").textContent =
        //     window.cartStrings.error
        this.disableLoading(line);
      });
  }

  open() {
    this.activeElement = document.activeElement || this.cartDrawerOpen;
    this.drawer.classList.add("animate", "active");

    this.drawer.addEventListener(
      "transitionend",
      () => {
        this.drawer.focus();
        trapFocus(this.drawer, this.cartDrawerClose);
      },
      { once: true },
    );

    document.body.classList.add("cart-drawer-open");
    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    this.drawer.classList.remove("active");

    document.body.removeEventListener("click", this.onBodyClick);
    document.body.classList.remove("cart-drawer-open");

    removeTrapFocus(this.activeElement);
  }

  renderContents(
    parsedState,
    addedItemResponse = false,
    line,
    name,
    isDrawerOpened = false,
  ) {
    const itemCount = Number(
      parsedState?.item_count ?? addedItemResponse?.item_count ?? 0,
    );

    this.classList.toggle("is-empty", itemCount === 0);
    this.itemCountHolder.innerText = itemCount;
    this.itemCountHolder.classList.toggle("is-active", itemCount > 0);
    const cartFooter = document.getElementById("cart-drawer-footer");

    if (cartFooter) cartFooter.classList.toggle("is-empty", itemCount === 0);

    let stateWithSections = null;
    if (addedItemResponse && addedItemResponse.sections) {
      stateWithSections = addedItemResponse;
    } else if (parsedState && parsedState.sections) {
      stateWithSections = parsedState;
    }

    if (!stateWithSections) {
      window.console.warn(
        "[Cart Drawer] Missing sections in render payload. Triggering refresh.",
      );
      this.disableLoading();
      this.refreshCart();
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      const sectionHtml = stateWithSections.sections?.[section.section];
      if (!sectionHtml) {
        window.console.warn(
          `[Cart Drawer] Section '${section.section}' missing in payload.`,
        );
      } else {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          sectionHtml,
          section.selector,
        );
      }
    });
    this.manageAddonsGiftboxVisibility();

    this.updateLiveRegions(line, itemCount);
    const lineItem = document.getElementById(`CartDrawerItem-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`))
      lineItem.querySelector(`[name="${name}"]`).focus();
    this.disableLoading();

    if (!isDrawerOpened) {
      if (this.header) this.header.reveal();
      this.open();
    }

    // Update property labels after rendering contents
    this.updatePropertyLabels();
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount) {
    if (line == "" || !line) {
      return;
    }

    if (this.currentItemCount === itemCount) {
      document
        .getElementById(`CartDrawerItem-error-${line}`)
        .querySelector(".cart-item__error-text").innerHTML =
        window.cartStrings.quantityError.replace(
          "[quantity]",
          document.getElementById(`CartDrawerItem-quantity-${line}`).value,
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus = document.getElementById("cart-drawer-live-region-text");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  enableLoading(line) {
    const mainDiv = this.querySelector(`#CartDrawerItem-${line}`);
    if (mainDiv) {
      const loadingElement = mainDiv.querySelector("[data-loading]");
      const qtyData = mainDiv.querySelectorAll("[data-qty]");
      const priceElement = mainDiv.querySelector(".p2--fixed");
      if (priceElement) {
        priceElement.classList.add("hidden");
      }
      qtyData.forEach((button) => (button.disabled = true));

      if (loadingElement) {
        loadingElement.classList.remove("hidden");
      }
    }
    document
      .getElementById("cart-drawer")
      .classList.add("cart-drawer--disabled");
    this.querySelectorAll(`#CartDrawerItem-${line} .loading-overlay`).forEach(
      (overlay) => overlay.classList.remove("hidden"),
    );
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line = null) {
    if (line) {
      const mainDiv = this.querySelector(`#CartDrawerItem-${line}`);
      if (mainDiv) {
        const loadingElement = mainDiv.querySelector("[data-loading]");
        const qtyData = mainDiv.querySelectorAll("[data-qty]");
        const priceElement = mainDiv.querySelector(".p2--fixed");
        if (priceElement) {
          priceElement.classList.remove("hidden");
        }

        qtyData.forEach((button) => (button.disabled = false));
        if (loadingElement) {
          loadingElement.classList.add("hidden");
        }
      }
    }
    document
      .getElementById("cart-drawer")
      .classList.remove("cart-drawer--disabled");
  }

  handleBodyClick(event) {
    const target = event.target;
    const isNotDrawer =
      target !== this.drawer && !target.closest("cart-drawer");
    const isNotButtonOpen =
      target !== this.cartDrawerOpen &&
      !target.closest('[data-js-cart-drawer="cartDrawerOpen"]');
    const isCloseButton =
      target === this.cartDrawerClose ||
      target.closest('[data-js-cart-drawer="cartDrawerClose"]');

    if ((isNotDrawer && isNotButtonOpen) || isCloseButton) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateTrappedFocus() {
    removeTrapFocus(document.activeElement);

    setTimeout(() => {
      this.setActiveElement(this.cartDrawerClose || this.cartDrawerOpen);
      this.drawer.focus();
      trapFocus(this.drawer, this.cartDrawerClose);
    });
  }

  // Remove "hide add ons" button for Original Newspapers,  if all of them are removed
  hideEmptyAccordion() {
    this.drawer
      .querySelectorAll(".addon-accordion-content-wrapper")
      .forEach((container) => {
        const hasFlexItems = Array.from(container.children).some((child) =>
          child.matches("div.addon-accordion-item[data-show-accordion-opener]"),
        );

        if (!hasFlexItems) {
          const accordionRoot = container.closest(".addon-accordion-root");
          if (accordionRoot) accordionRoot.style.display = "none";
        }
      });
  }

  /**
   * Function for refreshing the cart drawer when member store credit toggle changes
   */
  refreshCart() {
    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );
    const sectionsUrl = window.location.pathname;
    const body = JSON.stringify({
      updates: {},
      sections,
      sections_url: sectionsUrl,
    });

    if (typeof fetchConfig === "undefined") {
      window.console.error(
        "[Cart Drawer] fetchConfig is undefined.",
        FETCH_CONFIG_ERROR_STRING,
      );
      return Promise.resolve();
    }
    // Returned so callers (e.g. the add-on button) can await the re-render
    // before showing a notice inside the re-rendered contents.
    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        return window.CartAddonGuard.sweepThenRender(
          parsedState,
          { sections, sectionsUrl },
          (finalState) => {
            this.renderContents(finalState, false, null, null, true);
          },
        );
      })
      .catch((error) => {
        window.console.error("[Cart Drawer] Error refreshing cart:", error);
      });
  }

  manageAddonsGiftboxVisibility() {
    this.drawer
      .querySelectorAll(".addon-accordion-content-wrapper")
      .forEach((wrapper) => {
        const hasGiftbox = wrapper.querySelector(".added-addon-item--giftbox");

        if (hasGiftbox) {
          // Disable all non-added giftbox items in this wrapper
          wrapper
            .querySelectorAll(".not-added-addon-item--giftbox")
            .forEach((item) => {
              item.classList.add("disabled"); // Add a disabled class for styling
            });
        }
      });
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartAddonButtonExtended extends HTMLElement {
  constructor() {
    super();
    this.isProcessing = false;
    this.addEventListener("click", this.handleClick.bind(this));
  }

  async handleClick(event) {
    event.preventDefault();

    // Prevent concurrent requests on rapid clicks. aria-disabled also covers
    // the case where a sibling button of the same parent line is processing.
    if (this.isProcessing || this.getAttribute("aria-disabled") === "true")
      return;
    this.isProcessing = true;

    // Optional UX: disable the button while processing
    this.setAttribute("aria-disabled", "true");
    this.classList.add("is-loading");

    const cartDrawer = this.closest("cart-drawer");
    if (!cartDrawer) {
      console.error("[Add-on] Cart drawer not found");
      this._resetProcessingState();
      return;
    }

    const config = this.getConfiguration();
    if (!config.variantId) {
      console.error("[Add-on] Missing data-variation-id on button");
      this._resetProcessingState();
      return;
    }

    // isProcessing guards this element only; sibling add-on buttons of the
    // same parent line stay clickable during the request, which is how a
    // second giftbox variant could land in the same round trip. Disable them
    // for the duration (their click handler checks aria-disabled).
    const parentLineElement = this.closest(".cart-item");
    const siblingButtons = parentLineElement
      ? Array.from(
          parentLineElement.querySelectorAll("cart-addon-button"),
        ).filter((button) => button !== this)
      : [];
    siblingButtons.forEach((button) => {
      button.setAttribute("aria-disabled", "true");
      button.classList.add("is-loading");
    });

    try {
      cartDrawer.enableLoading(config.index);
      const res = await this.addAddonToCart(cartDrawer, config);
      cartDrawer.disableLoading(config.index);

      // Refresh first: the notices container lives inside .js-contents, so a
      // message shown before the re-render would be wiped by it.
      if (res?.refresh) {
        await cartDrawer.refreshCart();
      }

      if (res && res.success === false && res.message) {
        this.showCartDrawerError(res.message);
      }
    } catch (error) {
      console.error("[Add-on] Error adding add-on to cart:", error);
      cartDrawer.disableLoading(config.index);
      this.showCartDrawerError(error?.message);
    } finally {
      siblingButtons.forEach((button) => {
        button.removeAttribute("aria-disabled");
        button.classList.remove("is-loading");
      });
      this._resetProcessingState();
    }
  }

  _resetProcessingState() {
    this.isProcessing = false;
    this.removeAttribute("aria-disabled");
    this.classList.remove("is-loading");
  }

  getConfiguration() {
    return {
      variantId: this.dataset.variationId,
      // Render-time hint only. The authoritative parent id is resolved in
      // addAddonToCart from the fetched cart line itself: data-uuid can be
      // stale, and window.submissionId (the old fallback) belongs to whatever
      // PDP happens to be open, so a giftbox stamped with it referenced an id
      // no cart line carried and became an invisible, unremovable orphan.
      parentUuid: this.dataset.uuid || null,
      fieldName: this.dataset.fieldName || null,
      skuValue: this.dataset.skuValue || null,
      skuKey: this.dataset.skuKey || "giftbox",
      lineKey: this.dataset.addons || null,
      index: this.dataset.index || 1,
    };
  }

  async addAddonToCart(cartDrawer, config) {
    const returnObject = {
      success: false,
      refresh: false,
      message: null,
      step: "initial",
    };

    try {
      let cart;
      let revertMainProduct = false;

      try {
        cart = await cartDrawer.getCart();
        if (!cart) {
          console.error("[Add-on] Cart data not available when adding add-on.");
          returnObject.success = false;
          returnObject.refresh = false;
          returnObject.step = "fetch-cart";
          returnObject.message =
            "Cart data wasn’t available. Please refresh the page and try again.";
          return returnObject;
        }
      } catch (error) {
        console.error("[Add-on] Failed to retrieve cart for add-on:", error);
        returnObject.success = false;
        returnObject.refresh = false;
        returnObject.step = "fetch-cart-error";
        returnObject.message =
          "We couldn’t load your cart. Please refresh the page and try again.";
        return returnObject;
      }

      const mainItem = this.findMainItem(cart, config.lineKey);
      if (!mainItem) {
        console.error(
          `[Add-on] Could not find main product line in cart for add-on. Looking for lineKey: ${config.lineKey}`,
        );
        returnObject.success = false;
        returnObject.refresh = false;
        returnObject.step = "find-main-item";
        returnObject.message =
          "We couldn’t find the item you’re trying to update. Please refresh the page and try again.";
        return returnObject;
      }

      // The authoritative parent id is the line's own _main_id. When the
      // parent has none (external cart rebuilds such as "buy again" or cart
      // permalinks recreate lines without properties), mint one and write it
      // onto the parent in the same /cart/change that records the add-on
      // selection. Without the mint the giftbox would reference an id no
      // line carries and become an invisible, unremovable orphan.
      const mainProperties = mainItem.properties || {};
      const mintedParentId = !mainProperties._main_id
        ? window.CartAddonGuard.uuidv4()
        : null;
      const parentUuid = mintedParentId || mainProperties._main_id;

      // Duplicate guard: the same giftbox may already be linked to this
      // parent (rapid second click before the drawer re-renders, or a stale
      // drawer in another tab). The server-rendered addon_exists check in
      // cart-item.liquid only hides the button after a re-render, so check
      // the just-fetched cart too. Deliberately NOT keyed off the parent's
      // addon.<skuKey> property: removal flows reset it to sentinel values
      // ('No Presentation Box' / 'no-drinks') or leave it stale, so it would
      // permanently block re-adding.
      const duplicateAddon = cart.items.some(
        (item) =>
          (item.product_type || "").toLowerCase() === "addon" &&
          item.properties &&
          item.properties._addon_id === parentUuid &&
          !item.properties._main_id &&
          String(item.id) === String(config.variantId),
      );
      if (duplicateAddon) {
        returnObject.success = false;
        returnObject.refresh = true;
        returnObject.step = "duplicate-skipped";
        returnObject.message = "This add-on is already in your basket.";
        return returnObject;
      }

      const updatedMainProperties = this._buildMainProductProperties(
        mainItem,
        config,
        mintedParentId,
      );

      // /cart/change re-keys a line whenever its properties change, so any
      // later rollback must target the parent's NEW key, resolved from the
      // change response below. The pre-change mainItem is only the fallback.
      let revertTargetItem = mainItem;

      // 1) Update main product properties
      try {
        const mainProductUpdateResponse = await this.mainProductPropsUpdate(
          mainItem,
          updatedMainProperties.updated,
        );

        if (!mainProductUpdateResponse || !mainProductUpdateResponse.ok) {
          const msg = await this._getCartApiErrorMessage(
            mainProductUpdateResponse,
            "Something went wrong while updating your selection. Please refresh and try again.",
          );
          console.error(
            `[Add-on] Failed to update main product properties; skipping add-on: ${mainProductUpdateResponse?.status} - ${msg}`,
          );
          returnObject.success = false;
          returnObject.refresh = false;
          returnObject.step = "update-main-product";
          returnObject.message = msg;
          return returnObject;
        }

        const findParentByMainId = (cartJson) =>
          cartJson && Array.isArray(cartJson.items)
            ? cartJson.items.find(
                (item) =>
                  item.properties &&
                  item.properties._main_id === parentUuid &&
                  (item.product_type || "").toLowerCase() !== "addon",
              )
            : null;

        let reKeyedParent = findParentByMainId(
          await this._readResponseJsonSafe(mainProductUpdateResponse),
        );
        if (!reKeyedParent) {
          // The change response body was unreadable, and the pre-change key
          // is guaranteed stale (the change that just succeeded re-keyed the
          // line), so resolve the new key from a fresh cart fetch.
          try {
            reKeyedParent = findParentByMainId(await cartDrawer.getCart());
          } catch (freshCartError) {
            reKeyedParent = null;
          }
        }
        if (reKeyedParent) {
          revertTargetItem = reKeyedParent;
        }
      } catch (mainProductPropsUpdateFailed) {
        console.error(
          "[Add-on] Failed to update main product properties; skipping add-on:",
          mainProductPropsUpdateFailed,
        );
        returnObject.success = false;
        returnObject.refresh = false;
        returnObject.step = "update-main-product-error";
        returnObject.message =
          "Something went wrong while updating your selection. Please refresh and try again.";
        return returnObject;
      }

      // 2) Add the add-on
      try {
        const addonProperties = this._buildAddonProperties(parentUuid);
        const addAddonProductResponse = await this.addAddonProduct(
          config.variantId,
          addonProperties,
        );

        if (!addAddonProductResponse || !addAddonProductResponse.ok) {
          const msg = await this._getCartApiErrorMessage(
            addAddonProductResponse,
            "Rolling back your action. Something went wrong—please try again after refreshing the page.",
          );

          console.error(
            `[Add-on] Failed to add add-on: rollback main product properties: ${addAddonProductResponse?.status} - ${msg}`,
          );
          returnObject.success = false;
          returnObject.refresh = false;
          returnObject.message = msg;
          returnObject.step = "add-addon-product";
          revertMainProduct = true;
        }
      } catch (addAddonProductFailed) {
        console.error(
          "[Add-on] Failed to add add-on: rollback main product properties:",
          addAddonProductFailed,
        );
        returnObject.success = false;
        returnObject.refresh = false;
        returnObject.step = "add-addon-product-error";
        returnObject.message =
          "Rolling back your action. Something went wrong—please try again after refreshing the page.";
        revertMainProduct = true;
      }

      // If adding the add-on fails, roll back the main product properties to their previous state.
      if (revertMainProduct) {
        try {
          // /cart/change silently ignores an EMPTY properties object, so a
          // bare parent (external rebuild, properties were {}) cannot be
          // reverted to nothing. Keep the minted identity pair in that case
          // and only shed the addon./customisation. flags written above; a
          // parent carrying just _main_id/_addon_id with no add-on line is
          // consistent.
          let revertProperties = updatedMainProperties.current;
          if (Object.keys(revertProperties).length === 0) {
            revertProperties = {
              _main_id: parentUuid,
              _addon_id: parentUuid,
            };
          }
          const revertMainProductResponse = await this.mainProductPropsUpdate(
            revertTargetItem,
            revertProperties,
          );

          if (!revertMainProductResponse || !revertMainProductResponse.ok) {
            const revertMsg = await this._getCartApiErrorMessage(
              revertMainProductResponse,
              "We couldn't fully undo your last change. Please refresh the page and review your cart before continuing.",
            );
            console.error(
              `[Add-on] Rollback of main product properties failed. Cart may be in an inconsistent state: ${revertMainProductResponse?.status} - ${revertMsg}`,
            );

            returnObject.success = false;
            returnObject.refresh = false;
            returnObject.step = "revert-main-product";
            returnObject.message = revertMsg;
            return returnObject;
          }

          returnObject.success = false;
          // The rollback re-keyed the parent again: re-render so no remove
          // button or add-on button keeps stale data-addons keys.
          returnObject.refresh = true;
          returnObject.step = "revert-main-product-success";

          return returnObject;
        } catch (revertMainProductResponseFailed) {
          console.error(
            "[Add-on] Rollback of main product properties threw an error. Cart may be in an inconsistent state:",
            revertMainProductResponseFailed,
          );

          returnObject.success = false;
          returnObject.refresh = false;
          returnObject.step = "revert-main-product-error";
          returnObject.message =
            "Something went wrong while undoing your last change. Please refresh the page and review your cart before continuing.";
          return returnObject;
        }
      }

      returnObject.success = true;
      returnObject.refresh = true;
      returnObject.step = "completed";
      returnObject.message = "Cart updated successfully.";
      return returnObject;
    } catch (unexpectedError) {
      console.error(
        "[Add-on] addAddonToCart unexpected error:",
        unexpectedError,
      );
      returnObject.success = false;
      returnObject.refresh = false;
      returnObject.step = "unexpected-error";
      returnObject.message =
        "Something went wrong. Please refresh the page and try again.";
      return returnObject;
    }
  }

  findMainItem(cart, lineKey) {
    return cart.items.find(
      (item) =>
        item.key === lineKey && item.product_type?.toLowerCase() !== "addon",
    );
  }

  _buildMainProductProperties(mainItem, config, mintUuid) {
    // All properties are primitive values only.
    const propertiesPreviousState = { ...(mainItem.properties || {}) };
    const properties = { ...(mainItem.properties || {}) };

    if (mintUuid) {
      // Heal a parent that has no _main_id so the add-on can reference it.
      // _addon_id is written too for parity with the PDP path
      // (product-form.js appends both): cart-item.liquid classifies a line
      // as an add-on parent from that pair, and its remove-button logic
      // depends on the classification.
      properties._main_id = mintUuid;
      properties._addon_id = mintUuid;
    }

    if (config.fieldName) {
      properties[`customisation.${config.fieldName}`] = "true";
    }

    if (config.skuKey && config.skuValue) {
      properties[`addon.${config.skuKey}`] = config.skuValue;
    }

    return {
      updated: properties,
      current: propertiesPreviousState,
    };
  }

  _buildAddonProperties(parentUuid) {
    const properties = {
      _addon_id: parentUuid,
    };

    return properties;
  }

  async mainProductPropsUpdate(mainItem, properties) {
    const body = JSON.stringify({
      id: mainItem.key,
      quantity: mainItem.quantity,
      properties,
    });

    try {
      // IMPORTANT: do not need to throw on non-2xx inside handleFetchCall,
      // so we have to read Shopify’s 422 body (status/message/description). See https://shopify.dev/docs/api/ajax/reference/cart
      return await this.handleFetchCall(routes.cart_change_url, body);
    } catch (mainProductAddFetchCallError) {
      console.error(
        `[Add-on] Failed while updating main product: ${mainProductAddFetchCallError}`,
      );
      throw new Error(
        `[Add-on] Failed while updating main product: ${mainProductAddFetchCallError}`,
      );
    }
  }

  async addAddonProduct(variantId, properties) {
    const body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties,
        },
      ],
    });

    try {
      return await this.handleFetchCall(routes.cart_add_url, body);
    } catch (addonAddFetchCallError) {
      console.error(
        `[Add-on] Failed while adding add-on: ${addonAddFetchCallError}`,
      );
      throw new Error(
        `[Add-on] Failed while adding add-on: ${addonAddFetchCallError}`,
      );
    }
  }

  async handleFetchCall(url, body) {
    let response;
    if (typeof fetchConfig === "undefined") {
      throw new Error(FETCH_CONFIG_ERROR_STRING);
    }
    try {
      response = await fetch(url, {
        ...fetchConfig(),
        body,
      });
    } catch (networkError) {
      // Network / CORS / DNS / aborted request, etc.
      console.error("[Add-on] Fetch failed (network error):", networkError);
      throw networkError;
    }

    // Do not need to throw on non-2xx; we can have access to response.status + body
    // (especially for Shopify’s 422 structured errors)
    return response;
  }

  showCartDrawerError(message = "") {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;

    const infoEl = cartDrawer.querySelector(".cart-drawer__notices");
    if (!infoEl) return;

    let errorEl = infoEl.querySelector(".cart-drawer__error");
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.className = "cart-drawer__error";
      errorEl.setAttribute("role", "alert");
      errorEl.setAttribute("aria-live", "assertive");
    } else if (errorEl.parentElement === infoEl) {
      // Remove existing element so re-append triggers live region updates
      infoEl.removeChild(errorEl);
    }

    // Ensure string
    const safeMessage =
      typeof message === "string" && message.trim()
        ? message
        : "Something went wrong. Please refresh and try again.";

    errorEl.textContent = "";
    infoEl.appendChild(errorEl);
    errorEl.textContent = safeMessage;
  }

  /**
   * Shopify’s Cart Ajax API documents 422 errors as JSON:
   * { status: 422, message: "Cart Error", description: "..." }  (see https://shopify.dev/docs/api/ajax/reference/cart)
   * This helper tries hard to extract a useful message for the customer.
   */
  async _getCartApiErrorMessage(response, fallbackMessage) {
    if (!response) return fallbackMessage;

    if (response.status === 422) {
      const data = await this._readResponseJsonSafe(response);

      if (data && typeof data === "object") {
        // Shopify may return:
        // { description: "..." }
        // { errors: "..." }
        // { errors: ["...", "..."] }

        if (typeof data.description === "string" && data.description.trim()) {
          return data.description;
        }

        if (typeof data.errors === "string" && data.errors.trim()) {
          return data.errors;
        }

        if (Array.isArray(data.errors) && data.errors.length > 0) {
          const errorMessages = data.errors.filter(
            (e) => typeof e === "string" && e.trim(),
          );
          if (errorMessages.length > 0) {
            return errorMessages.join(", ");
          }
        }

        if (typeof data.message === "string" && data.message.trim()) {
          return data.message;
        }
      }

      return "This item can’t be added to your cart right now. Please refresh and try again.";
    }

    if (response.status === 404) {
      const data = await this._readResponseJsonSafe(response);
      if (
        data &&
        typeof data.description === "string" &&
        data.description.trim()
      ) {
        return data.description;
      }
      return "We couldn’t find that item. Please refresh and try again.";
    }

    return fallbackMessage;
  }

  _safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async _readResponseTextSafe(response) {
    if (!response) return "";

    // Return cached text if already read
    const cached = response[RESPONSE_TEXT_CACHE_KEY];
    if (typeof cached === "string") return cached;

    // Prefer reading from a clone to avoid consuming the original stream
    try {
      const text = await response.clone().text();
      response[RESPONSE_TEXT_CACHE_KEY] = text;
      return text;
    } catch (cloneReadError) {
      console.error("[Add-on] Failed to clone response:", cloneReadError);
      // If clone fails (can happen if body already used), try reading original once
      try {
        const text = await response.text();
        response[RESPONSE_TEXT_CACHE_KEY] = text;
        return text;
      } catch (originalReadError) {
        console.error(
          "[Add-on] Failed to read response text:",
          originalReadError,
        );
        // Cache empty string so we don't repeatedly attempt reads
        response[RESPONSE_TEXT_CACHE_KEY] = "";
        return "";
      }
    }
  }

  async _readResponseJsonSafe(response) {
    if (!response) return null;

    // Use cached text path so JSON/text reads never fight each other
    const text = await this._readResponseTextSafe(response);
    if (!text) return null;

    return this._safeJsonParse(text);
  }
}

customElements.define("cart-addon-button", CartAddonButtonExtended);
